import React, { useState, useMemo } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  FormikSelect,
  FormikCheckBox,
  FormikInput,
  FormikRangePicker,
  EmployeeSearchDataTable
} from '../../../components';
import { statuses, priorities } from './FormOptions';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';

const selectorTypes = [{ label: 'Todos', value: 'all_employees' }];

const CompanyTaskForm = ({
  errors,
  touched,
  values,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  companyTask,
  isEmployee,
  rrhh,
  currentEmployee: currentEmployeeProp
}) => {
  const { startDate, endDate, status, priority } = values.companyTask;
  const [selector, setSelector] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const { currentEmployee: currentEmployeeAuth } = useSelector(state => state.auth);
  const currentEmployee = currentEmployeeProp || currentEmployeeAuth;
  const customParams = useMemo(() => (rrhh ? {} : { boss_and_employees: currentEmployee }), [currentEmployee, rrhh]);

  const handleSuccessEmployees = response => {
    const vEmployees = camelCaseEmptyStringRecursive(response?.data?.data) || [];
    const { employees } = companyTask;
    const employeeIds = employees.map(item => item.id);

    const employeesToAdd = vEmployees.filter(selected => !employeeIds.includes(selected.id));
    const vSelector = [...selectorTypes, ...employeesToAdd];
    setSelector(vSelector);
  };

  const removeFromTable = (employee, action) => {
    if (action === 'destroy') {
      const { employees: companyTaskEmployees } = values.companyTask;
      const notRemovedEmployees = companyTaskEmployees.filter(selected => selected.id !== employee.id);
      const addToSelector = [...selector, employee];
      setSelector(addToSelector);
      setFieldValue('companyTask[employees]', notRemovedEmployees);
      if (notRemovedEmployees.length === 0) setFieldValue('companyTask[employeeIds]', ['']);
      else
        setFieldValue(
          'companyTask[employeeIds]',
          notRemovedEmployees.map(item => item.id)
        );
    }
  };

  const removeAllFromTable = () => {
    const selectedRowsIds = selectedRows.map(item => item.id);
    const notRemovedEmployees = values.companyTask.employees.filter(selected => !selectedRowsIds.includes(selected.id));
    setSelector([...selectorTypes, ...selector, ...selectedRows].filter(item => item.label));
    setFieldValue('companyTask[employees]', notRemovedEmployees);
    setFieldValue(
      'companyTask[employeeIds]',
      notRemovedEmployees.map(item => item.id)
    );
    setSelectedCount(0);
    setSelectedRows([]);
  };

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const addAllEmployees = () => {
    const { companyTask: companyTaskValues } = values;
    const vSelector = selector.filter(selected => selected.id !== undefined);
    const selectedEmployeeIds = vSelector.map(employee => employee.value);
    const selectedEmployees = vSelector.map(employee => ({ ...employee }));
    setSelector([selectorTypes]);
    setFieldValue('employeeSelector', '');
    setFieldValue('companyTask[employees]', [...selectedEmployees, ...companyTaskValues.employees]);
    setFieldValue('companyTask[employeeIds]', [...selectedEmployeeIds, ...companyTaskValues.employeeIds]);
  };

  const handleSelector = () => {
    const vSelector = selector.filter(selected => selected.value !== values.employeeSelector);
    if (vSelector.length === selector.length) {
      return;
    }
    if (values.employeeSelector === 'all_employees') {
      addAllEmployees();
    } else {
      const employeeToAdd = selector.find(selected => selected.value === values.employeeSelector);
      setFieldValue(`companyTask[employeeIds]`, [...values.companyTask.employeeIds, employeeToAdd.value]);
      setFieldValue('companyTask[employees]', [...values.companyTask.employees, employeeToAdd]);
      setSelector(vSelector);
    }
  };

  const handleSearch = selectedEmployees => {
    const vSelector = selector.filter(selected => !selectedEmployees.includes(selected.value));
    if (vSelector.length === selector.length) return;
    const employeesToAdd = selector.filter(selected => selectedEmployees.includes(selected.value));
    setFieldValue(`companyTask[employeeIds]`, [
      ...values.companyTask.employeeIds,
      ...employeesToAdd.map(item => item.value)
    ]);
    setFieldValue('companyTask[employees]', [...values.companyTask.employees, ...employeesToAdd]);
    setSelector(vSelector);
  };

  return (
    <>
      <div className="yellow-wrapper">
        <Form id="task-form" className="form-task p-2">
          <Row>
            <Col md={isEmployee ? 12 : 3}>
              <Field name="companyTask[title]">
                {({ field }) => (
                  <FormikInput
                    {...field}
                    abbr
                    label="Titulo"
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={isEmployee ? 12 : 3}>
              <Field name="rangeDate">
                {({ field }) => (
                  <FormikRangePicker
                    {...field}
                    abbr
                    startDateName="companyTask[startDate]"
                    endDateName="companyTask[endDate]"
                    startDate={startDate}
                    endDate={endDate}
                    showClearDates
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={isEmployee ? 12 : 3}>
              <Field name="companyTask[priority]">
                {({ field }) => (
                  <FormikSelect
                    {...field}
                    abbr
                    label="Prioridad"
                    placeholder="Seleccionar prioridad"
                    defaultValue={priority}
                    options={priorities}
                    onChange={data => setFieldValue(field.name, data ? data.value : '')}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={isEmployee ? 12 : 3}>
              <Field name="companyTask[status]">
                {({ field }) => (
                  <FormikSelect
                    {...field}
                    abbr
                    label="Estado"
                    placeholder="Seleccionar estado"
                    options={statuses}
                    defaultValue={status}
                    onChange={data => setFieldValue(field.name, data ? data.value : '')}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          </Row>
          <Row>
            {!isEmployee && (
              <Col md={6}>
                <Row>
                  <Col md={6}>
                    <Field name="companyTask[sendEmail]">
                      {({ field }) => (
                        <FormikCheckBox
                          {...field}
                          field={field}
                          id="sendEmail"
                          label="Enviar Email"
                          checked={field.value}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col md={6}>
                    <Field name="companyTask[sendNotification]">
                      {({ field }) => (
                        <FormikCheckBox
                          {...field}
                          field={field}
                          id="sendNotification"
                          label="Enviar Notificación"
                          checked={field.value}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
          <Row>
            <Col md={12}>
              <Field name="companyTask[description]">
                {({ field }) => (
                  <FormikInput
                    {...field}
                    as="textarea"
                    label="Descripción"
                    minInputHeight="120"
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          </Row>
        </Form>

        <hr className="yellow mt-2 mb-3" />

        {!isEmployee && (
          <>
            <EmployeeSearchDataTable
              baseModel={companyTask}
              employees={values.companyTask.employees}
              handleActions={removeFromTable}
              handleMassAction={removeAllFromTable}
              handleSearch={handleSearch}
              handleSelectedRows={handleSelectedRows}
              handleSelector={handleSelector}
              handleSuccessEmployees={handleSuccessEmployees}
              options={selector}
              selectedCount={selectedCount}
              advanceSearch={rrhh}
              customParams={customParams}
              label="Agregar Trabajadores"
            />
          </>
        )}
        <Row className={`mt-4 ${!isEmployee ? 'mb-4' : ''}`}>
          <Col className="d-flex justify-content-end">
            <Button type="submit" form="task-form" disabled={isSubmitting} variant="primary">
              GUARDAR
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );
};

const setInitialValues = props => {
  const { employeeIds, endDate, startDate, sendEmail, sendNotification } = props.companyTask;
  const { selectedDate, notSendNotification, currentEmployee } = props;

  return {
    companyTask: {
      ...props.companyTask,
      employeeIds: selectedDate ? [currentEmployee] : employeeIds,
      endDate: selectedDate || endDate,
      startDate: selectedDate || startDate,
      sendEmail: notSendNotification ? false : sendEmail,
      sendNotification: notSendNotification ? false : sendNotification
    },
    employeeSelector: 'all_employees',
    rangeDate: [startDate || selectedDate, endDate || selectedDate]
  };
};

const validationSchema = Yup.object().shape({
  companyTask: Yup.object().shape({
    description: Yup.string(),
    priority: Yup.string().required('Debes seleccionar una prioridad'),
    status: Yup.string().required('Debes seleccionar un estado'),
    title: Yup.string()
      .max(50, 'Deben ser menos que 50 caracteres')
      .required('Debes ingresar un título')
  }),
  rangeDate: Yup.array().rangedate(true)
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(CompanyTaskForm);
